<template>
  <section class="mt-2">
    <div class="mb-2 flex items-center justify-between">
      <h2
        class="text-[15.5px] font-extrabold tracking-[0.015em] text-[#2e234b] md:text-[18.56px] md:tracking-[0.02em]"
      >
        {{ title }}
      </h2>
      <button
        v-if="showSeeAll"
        class="bg-transparent text-[14px] font-extrabold text-[#2e234b] md:text-[18.56px]"
        @click="seeAll"
      >
        {{ $t("cardGrid.seeAll") }}
      </button>
    </div>

    <div
      v-if="loading && items.length === 0"
      class="grid gap-4 md:gap-6"
      :class="
        singleColumnOnMobile
          ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 [@media(min-width:430px)]:grid-cols-2'
          : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
      "
      aria-label="Loading cards"
    >
      <div
        v-for="n in skeletonCount"
        :key="`skeleton-${n}`"
        class="w-full max-w-sm animate-pulse"
      >
        <div class="aspect-square rounded-3xl bg-gray-200"></div>
        <div class="space-y-2 px-1 pt-4">
          <div class="h-4 w-5/6 rounded bg-gray-200"></div>
          <div class="h-3 w-1/2 rounded bg-gray-200"></div>
          <div class="mt-3 flex justify-between gap-2">
            <div class="h-3 w-2/5 rounded bg-gray-200"></div>
            <div class="h-3 w-1/4 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Responsive Grid -->
    <div
      v-else
      class="grid gap-4 md:gap-6"
      :class="
        singleColumnOnMobile
          ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 [@media(min-width:430px)]:grid-cols-2'
          : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
      "
    >
      <template v-for="it in items" :key="String(it.id ?? JSON.stringify(it))">
        <slot name="item" :item="it">
          <component :is="itemComponent || RevampedEventCard" v-bind="it" />
        </slot>
      </template>
    </div>

    <!-- Empty State -->
    <div
      v-if="items.length === 0 && !loading"
      class="mt-6 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-zinc-200 p-8 text-center text-zinc-600"
    >
      <slot name="empty">
        <h3 class="text-lg font-semibold text-indigo-950">
          {{ $t("cardGrid.noItemsFound") }}
        </h3>
        <p class="text-sm">{{ $t("cardGrid.tryWideningSearch") }}</p>
      </slot>
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore && items.length > 0" class="mt-8 flex justify-center">
      <button
        class="rounded-xl bg-[#412c71] px-8 py-3 font-semibold text-white transition-colors hover:bg-[#351f5c] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? $t("cardGrid.loading") : $t("cardGrid.loadMore") }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import RevampedEventCard from "~/components/cards/EventCard.vue";

const skeletonCount = 10;

type GridItem = {
  id?: string | number;
} & Record<string, unknown>;

const props = defineProps<{
  title: string;
  items: GridItem[];
  railKey?: string;
  hasMore?: boolean;
  loading?: boolean;
  itemComponent?: unknown;
  showSeeAll?: boolean;
  singleColumnOnMobile?: boolean;
}>();

const emit = defineEmits<{
  (e: "seeAll", key: string): void;
  (e: "loadMore"): void;
}>();

function seeAll() {
  emit("seeAll", props.railKey ?? "");
}

function loadMore() {
  if (props.loading) return;
  emit("loadMore");
}
</script>
